import { useQuery } from "@apollo/client";
import type {
  MultipleProductCard,
  SearchProducts,
  SingularProductCard,
  VariantType
} from "@shared/graphql.types";
import { colours } from "@shared/ui.design-system";
import { ChevronRightIcon, CloseIcon } from "@shared/ui.icons";
import { Button, Drawer, Typography } from "@shared/ui.primitives";
import { navigate } from "@shared/utils.routing";
import cookies from "js-cookie";
import { useParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ATBConfirmation } from "basket_and_checkout/shared/components/ATBConfirmation";
import { DefaultComponentOnSearch } from "components/Layouts/Header/Search/DefaultComponentOnSearch";
import { QuizInsert } from "components/Layouts/Header/Search/QuizInsert";
import { SEARCH_SUGGESTION_QUERY } from "data/plp/gql";
import { useChatVisibility } from "hooks";
import { useDebounce } from "hooks/useDebounce";
import { getClient } from "lib/apolloClient";
import { useUser } from "lib/user";
import { getTypicalJourneyStatus } from "packages/acquisition/GuestPrice/lib/typicalJourney";
import { ProductCardWrapper } from "packages/acquisition/shared/components/ProductCardWrapper/ProductCardWrapper";
import { useSegment } from "tracking";
import { trackSearch } from "utils/gtm";

import type { ProductCardTrackingPayload } from "packages/acquisition/shared/components/ProductCardWrapper/types";
import { toVariantType } from "packages/core-shopping/shared/toVariantType";

import { SearchBar } from "./SearchBar/SearchBar";
import * as styles from "./SearchV2.css";
import {
  isSearchError,
  isSearchSuggestionsResult,
  sanitizeQueryStringParam
} from "./utils";

type SearchResults = {
  products: (MultipleProductCard | SingularProductCard)[];
};

const emptySearchResults = {
  products: []
};

const DEBOUNCE_INTERVAL = 400;
const MIN_SEARCH_LENGTH = 2;

export const SearchV2: React.FC<{
  isSearchOpen: boolean;
  closeSearch: () => void;
}> = ({ isSearchOpen, closeSearch }) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] =
    useState<SearchResults>(emptySearchResults);

  const { track } = useSegment();

  const { isLoggedIn } = useUser();

  const debouncedSearchValue = useDebounce(searchValue, DEBOUNCE_INTERVAL);

  // N.B: We need to update src/packages/core-shopping/shared/components/Search/Search.test.tsx mocks once useGuestPrice changes 👇
  // Relevant code can be found in src/packages/core-shopping/shared/components/Search/Search.test.tsx with this tag: [remove-use-guest-price]
  useChatVisibility(isSearchOpen);

  const sanitizeSearchValue = useCallback(
    () => sanitizeQueryStringParam(searchValue),
    [searchValue]
  );

  const urlParams = useParams();
  const allProductsHref = useMemo(() => {
    const href = `shop/search?term=${sanitizeSearchValue()}`;

    if (urlParams.store === "us") {
      return `/us/${href}`;
    }

    return `/${href}`;
  }, [urlParams, sanitizeSearchValue]);

  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [confirmationVariant, setConfirmationVariant] =
    useState<VariantType | null>(null);

  const isATBVisible = confirmation && confirmationVariant !== null;
  const { data, error, loading } = useQuery<{ searchProducts: SearchProducts }>(
    SEARCH_SUGGESTION_QUERY,
    {
      variables: {
        term: debouncedSearchValue,
        isTypicalJourney: getTypicalJourneyStatus(cookies.get())
      },
      client: getClient(),
      skip: debouncedSearchValue.length < MIN_SEARCH_LENGTH
    }
  );

  useEffect(() => {
    // todo: implement a loading spinner
    if (loading) return;
    // todo: implement an error message
    if (error) return setSearchResults(emptySearchResults);

    const searchSuggestions = data?.searchProducts?.searchSuggestions;

    if (isSearchSuggestionsResult(searchSuggestions)) {
      return setSearchResults({
        products: searchSuggestions?.products ?? []
      });
    }

    if (isSearchError(searchSuggestions)) {
      // todo: implement an error message
      return setSearchResults(emptySearchResults);
    }
  }, [data, error, loading]);

  const setSearchAndSuggest = (value) => {
    setSearchValue(value);
    if (value.length < MIN_SEARCH_LENGTH) {
      setSearchResults(emptySearchResults);
    }
  };

  const onSubmit = () => {
    const sanitizedSearchValue = sanitizeSearchValue();

    track(
      {
        event: "Products Searched",
        properties: {
          query: sanitizedSearchValue
        }
      },
      {
        uiEvent: {
          target: "search button"
        }
      }
    );
    trackSearch({
      resultContent: `/shop/search?term=${sanitizedSearchValue}`,
      resultReturned: !!searchResults.products.length, // add suggested categories
      label: "suggested keyword",
      value: sanitizedSearchValue
    });
    closeSearch();
    navigate(`/shop/search?term=${sanitizedSearchValue}`);
  };

  const onClose = () => {
    // reset input value and close search panel
    track({
      event: "Page Element Clicked",
      properties: {
        module_content: "Close Search",
        module_content_details: searchValue,
        module_content_details2: `${searchResults.products.length}`
      }
    });
    setSearchValue("");
    setSearchResults(emptySearchResults);
    closeSearch();
  };

  const handleShowAllProducts = () => {
    const sanitizedSearchValue = sanitizeSearchValue();
    track(
      {
        event: "Products Searched",
        properties: {
          query: sanitizedSearchValue
        }
      },
      {
        uiEvent: {
          target: "show all products button"
        }
      }
    );
    trackSearch({
      resultContent: `/shop/search?term=${sanitizedSearchValue}`,
      resultReturned: true,
      label: "suggested keyword",
      value: sanitizedSearchValue
    });
    onClose();
  };
  const onFocus = async () => {
    track({
      event: "Search Input Focus",
      properties: {}
    });
  };

  const componentToShow = isLoggedIn ? (
    <DefaultComponentOnSearch />
  ) : (
    <QuizInsert />
  );

  return (
    <>
      {isATBVisible && (
        <ATBConfirmation
          confirmation={confirmation}
          setConfirmation={setConfirmation}
          variant={confirmationVariant}
          source="Search"
        />
      )}
      <Drawer
        isOpen={isSearchOpen}
        handleDismiss={onClose}
        ariaLabel="Search Panel"
        ariaDescription="Search Panel"
        size="fill"
        id="search-drawer"
        headerId="search-drawer-header"
      >
        <Drawer.Header id="search-drawer-header">
          <div
            className={styles.header}
            data-testid="search-panel-v2"
            id="search-panel"
          >
            <SearchBar
              value={searchValue}
              onChange={setSearchAndSuggest}
              onSubmit={onSubmit}
              onFocus={onFocus}
              onBack={onClose}
              placeholder="By: name, ingredient, usage..."
              isSearchOpen={isSearchOpen}
            />
            <CloseIcon
              width={24}
              height={24}
              data-testid="close-search"
              aria-label="Close Icon"
              className={styles.closeIcon}
              onClick={onClose}
            />
          </div>
        </Drawer.Header>

        <Drawer.Body>
          <div className={styles.body}>
            {!searchResults.products.length ? (
              componentToShow
            ) : (
              <>
                <Typography variant="subheader-3">Matching Products</Typography>
                <div className={styles.productCardLayout}>
                  {searchResults.products.map(
                    (productCard, index, allItems) => {
                      const beforeAdd = () => {
                        const isMultivariant =
                          productCard.type === "multivariate";
                        if (isMultivariant) {
                          navigate(`/products/${productCard.variants[0].slug}`);
                          return;
                        }
                        closeSearch?.();
                      };
                      const afterAdd = () => {
                        const variant = toVariantType(
                          productCard.variants[0],
                          productCard
                        );
                        setConfirmation?.(true);
                        setConfirmationVariant?.(variant);
                      };
                      const productClickedTrackingPayload: ProductCardTrackingPayload =
                        {
                          resultType: "suggestions",
                          contextOverride: {
                            search: { term: debouncedSearchValue },
                            product: {
                              list: {
                                targetPosition: index,
                                items: allItems.map((card) => ({
                                  sku: card.variants[0]?.sku
                                }))
                              }
                            }
                          }
                        };

                      return (
                        <div
                          key={`search-product-card-${productCard.id}`}
                          className={styles.productCardWrapper}
                          data-event-intersection="productImpression"
                          data-sku={productCard.variants[0].sku}
                          data-testid="search-product-card"
                        >
                          <ProductCardWrapper
                            productCard={productCard}
                            location="Search Modal"
                            enableShadeSelector={false}
                            addToBasketOptions={{
                              beforeAdd,
                              afterAdd
                            }}
                            onClickOptions={{ productClickedTrackingPayload }}
                          />
                        </div>
                      );
                    }
                  )}
                </div>
              </>
            )}
          </div>
        </Drawer.Body>
        {searchResults.products.length > 0 && (
          <Drawer.Footer>
            <a
              className={styles.showAllButton}
              data-testid="ShowAllProducts"
              href={allProductsHref}
              onClick={() => {
                handleShowAllProducts();
              }}
            >
              <Button
                text="Show all products"
                ariaLabel="Show all products"
                variant="primary"
                type="submit"
                rightIcon={<ChevronRightIcon color={colours.secondary.light} />}
              />
            </a>
          </Drawer.Footer>
        )}
      </Drawer>
    </>
  );
};
